// Lightweight PostHog wrapper. Loads + initialises PostHog only when
// VITE_POSTHOG_KEY is set, so local dev and key-less builds are no-ops (and
// don't pull posthog-js into the initial bundle). A per-browser UUID is
// persisted to localStorage and used as the anonymous identity.

type PostHog = typeof import('posthog-js')['default'];

const UID_KEY = 'beatswarm_uid';
const POSTHOG_HOST = 'https://us.i.posthog.com';

let ph: PostHog | null = null;

let sessionStart = 0;
let playStart: number | null = null;
let playMs = 0;
let hadCollab = false;

function flushSession(): void {
  if (!ph || !sessionStart) return;
  // Snapshot without stopping the play clock: the tab may be hidden while
  // playback continues (visibilitychange fires flushSession), and a later
  // flush must still count the time played after the tab was hidden.
  const livePlayMs = playStart !== null ? Date.now() - playStart : 0;
  try {
    ph.capture('session_ended', {
      duration_seconds: Math.round((Date.now() - sessionStart) / 1000),
      play_time_seconds: Math.round((playMs + livePlayMs) / 1000),
      had_collab: hadCollab,
    });
  } catch { /* swallow */ }
}

export function recordPlayStart(): void {
  if (playStart === null) playStart = Date.now();
}

export function recordPlayStop(): void {
  if (playStart !== null) {
    playMs += Date.now() - playStart;
    playStart = null;
  }
}

export function setCollabActive(): void {
  hadCollab = true;
}

function getOrCreateUid(): string {
  try {
    const existing = localStorage.getItem(UID_KEY);
    if (existing) return existing;
    const uid =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            const v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
          });
    localStorage.setItem(UID_KEY, uid);
    return uid;
  } catch {
    // localStorage unavailable (private mode etc.) — fall back to a volatile id.
    return 'anon-' + Math.random().toString(36).slice(2);
  }
}

export function initAnalytics(): void {
  const key = import.meta.env.VITE_POSTHOG_KEY;
  if (!key) return;
  import('posthog-js')
    .then(({ default: posthog }) => {
      posthog.init(key, {
        api_host: POSTHOG_HOST,
        capture_pageview: true,
        autocapture: false,
        persistence: 'localStorage',
      });
      posthog.identify(getOrCreateUid());
      ph = posthog;
      sessionStart = Date.now();
      window.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') flushSession();
      });
      window.addEventListener('beforeunload', flushSession);
    })
    .catch((err) => console.warn('PostHog load failed', err));
}

export function track(event: string, props?: Record<string, unknown>): void {
  if (!ph) return;
  try {
    ph.capture(event, props);
  } catch {
    // Swallow — analytics must never break the app.
  }
}
